import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useLeadQueueOverview, type BookerQueue } from "@/hooks/use-lead-queue";
import { cn } from "@/lib/utils";
import LeadQueueSettingsDialog from "./LeadQueueSettingsDialog";

/** Stolpen: ringbare nå mot strekene for lavvann og høyvann. */
function QueueBar({ q }: { q: BookerQueue }) {
  const max = Math.max(q.high_water, q.callable, 1);
  const pct = (n: number) => `${Math.min(100, (n / max) * 100)}%`;
  return (
    <div className="relative h-2 w-full bg-muted" aria-hidden>
      <div className={cn("h-2", q.callable < q.low_water ? "bg-foreground/40" : "bg-foreground")} style={{ width: pct(q.callable) }} />
      <span className="absolute -top-1 h-4 w-px bg-foreground" style={{ left: pct(q.low_water) }} />
      <span className="absolute -top-1 h-4 w-px bg-foreground" style={{ left: pct(q.high_water) }} />
    </div>
  );
}

/**
 * Bookerne i Leadlister (VIB-3050, skjerm 1): køen mot lavvann og høyvann, og poolene den fylles fra.
 * Klikk åpner «Bookerens kø og valg».
 */
export default function LeadQueueOverview() {
  const { data: bookers = [], isLoading } = useLeadQueueOverview();
  const { data: lists = [] } = useLeadLists();
  const [editing, setEditing] = useState<BookerQueue | null>(null);
  const poolName = new Map(lists.map((l) => [l.id, l.name]));

  const sources = (b: BookerQueue) =>
    [...b.own_list_names, ...(b.pool_ids === null ? ["alle felles pooler"] : b.pool_ids.map((id) => poolName.get(id) ?? "ukjent pool"))].join(", ");

  return (
    <section aria-label="Bookere" className="space-y-3">
      <h2 className="font-display text-xl font-semibold">Bookere</h2>
      {isLoading ? (
        <Skeleton className="h-28 w-full" />
      ) : bookers.length === 0 ? (
        <p className="border border-border p-6 text-sm text-muted-foreground">Ingen bookere har Leads.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {bookers.map((b) => (
            <button
              key={b.user_id}
              type="button"
              onClick={() => setEditing(b)}
              aria-label={`${b.full_name}: ${b.callable} ringbare, lavvann ${b.low_water}, høyvann ${b.high_water}. Endre kø og pooler`}
              className="space-y-2 border-[1.5px] border-foreground bg-card p-3 text-left hover:bg-muted/40"
            >
              <span className="block font-semibold">{b.full_name}</span>
              <span className="block truncate text-xs text-muted-foreground">{sources(b)}</span>
              <span className="block text-sm">Køen: {b.callable} ringbare</span>
              <QueueBar q={b} />
              <span className="flex justify-between text-xs text-muted-foreground">
                <span>lavvann {b.low_water}</span>
                <span>høyvann {b.high_water}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <LeadQueueSettingsDialog booker={editing} onClose={() => setEditing(null)} />
    </section>
  );
}
